import type { TypedSupabaseClient } from '../client';
import type {
  IPaymentCategoryRepository,
  PaymentCategory,
  CreatePaymentCategoryInput,
  UpdatePaymentCategoryInput,
} from '../../../interfaces/src';

export class SupabasePaymentCategoryRepository implements IPaymentCategoryRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(includeInactive = false): Promise<PaymentCategory[]> {
    let query = this.supabase
      .from('payment_categories')
      .select('*')
      .order('display_order', { ascending: true })
      .order('name', { ascending: true });
    if (!includeInactive) query = query.eq('is_active', true);
    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []).map(mapCategory);
  }

  async getById(id: string): Promise<PaymentCategory | null> {
    const { data, error } = await this.supabase
      .from('payment_categories')
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapCategory(data);
  }

  async create(input: CreatePaymentCategoryInput): Promise<PaymentCategory> {
    const { data, error } = await this.supabase
      .from('payment_categories')
      .insert({
        code: input.code,
        name: input.name,
        display_order: input.displayOrder ?? 0,
        is_active: input.isActive ?? true,
      } as never)
      .select()
      .single();
    if (error) throw error;
    return mapCategory(data);
  }

  async update(id: string, input: UpdatePaymentCategoryInput): Promise<PaymentCategory> {
    const patch: Record<string, unknown> = {};
    if (input.code !== undefined) patch['code'] = input.code;
    if (input.name !== undefined) patch['name'] = input.name;
    if (input.displayOrder !== undefined) patch['display_order'] = input.displayOrder;
    if (input.isActive !== undefined) patch['is_active'] = input.isActive;

    const { data, error } = await this.supabase
      .from('payment_categories')
      .update(patch as never)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapCategory(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase.from('payment_categories').delete().eq('id', id);
    if (error) throw error;
  }
}

function mapCategory(row: Record<string, unknown>): PaymentCategory {
  return {
    id: row['id'] as string,
    code: row['code'] as string,
    name: row['name'] as string,
    displayOrder: (row['display_order'] as number) ?? 0,
    isActive: (row['is_active'] as boolean) ?? true,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}
